import heroBrasileirao from "@/assets/heroes/brasileirao.webp";
import heroLibertadores from "@/assets/heroes/libertadores.webp";
import heroChampions from "@/assets/heroes/champions.webp";
import heroPaulistaoA1 from "@/assets/heroes/paulistao-a1.webp";
import heroCopaDoMundo from "@/assets/heroes/copa-do-mundo-fifa.webp";
import heroCopaDoBrasil from "@/assets/heroes/copa-do-brasil.webp";
import heroMundialDeClubes from "@/assets/heroes/mundial-de-clubes-fifa.webp";

/**
 * IDs estáveis e internos das competições que têm Hero própria (item 13:
 * nunca usar o nome visual da liga pra identificar a imagem). Cada entrada
 * aqui é independente do `competitionId` REAL salvo na liga (que vem da
 * API de futebol, ex: "BSA", "CL") — o mapeamento entre um e outro fica
 * em `COMPETITION_ID_TO_HERO_KEY` logo abaixo, só nesse arquivo.
 */
export type HeroKey =
  | "brasileirao"
  | "copa_do_brasil"
  | "libertadores"
  | "paulista_a1"
  | "champions"
  | "copa_do_mundo"
  | "mundial_de_clubes";

/**
 * Mapa HeroKey → arquivo da imagem. Só entra aqui quando a imagem real for
 * fornecida (item 26 — nunca gerar arte nova, só otimizar o arquivo
 * recebido). Enquanto uma competição não tiver imagem própria, ela
 * simplesmente não aparece neste objeto e cai no fallback (ver
 * `getCompetitionHero`).
 */
const HERO_IMAGES: Partial<Record<HeroKey, string>> = {
  brasileirao: heroBrasileirao,
  libertadores: heroLibertadores,
  champions: heroChampions,
  paulista_a1: heroPaulistaoA1,
  copa_do_mundo: heroCopaDoMundo,
  copa_do_brasil: heroCopaDoBrasil,
  mundial_de_clubes: heroMundialDeClubes,
};

/**
 * Mapeia o `competitionId` REAL (o que já vem salvo em cada liga, hoje o
 * código da football-data.org como "BSA"/"CL", ou o id interno da
 * API-Football como "PAULISTA_A1") pra um HeroKey estável. Ponto único de
 * verdade — se o código real de uma competição mudar de fonte um dia, só
 * conserta aqui, nenhuma liga precisa ser tocada (item 17).
 *
 * IMPORTANTE: os valores abaixo marcados "a confirmar" são o código mais
 * provável pela documentação da football-data.org, mas não foram
 * verificados ao vivo neste projeto (mesma disciplina já usada com a
 * API-Football — nunca cravar sem confirmar). Se algum não bater, é só
 * ajustar aqui.
 */
const COMPETITION_ID_TO_HERO_KEY: Record<string, HeroKey> = {
  BSA: "brasileirao", // Campeonato Brasileiro Série A (football-data.org) — confirmado no código
  CL: "champions", // UEFA Champions League (football-data.org) — a confirmar
  WC: "copa_do_mundo", // Copa do Mundo FIFA (football-data.org) — a confirmar
  PAULISTA_A1: "paulista_a1", // API-Football — já confirmado (etapa anterior)
  COPA_DO_BRASIL: "copa_do_brasil", // API-Football — já confirmado, hoje desativado
  CLI: "libertadores", // Copa Libertadores (football-data.org) — confirmado no Firestore da liga real
};

export function getHeroKeyForCompetition(competitionId: string): HeroKey | null {
  return COMPETITION_ID_TO_HERO_KEY[competitionId] ?? null;
}

/**
 * Retorna a URL da imagem Hero pra um `competitionId`, ou `null` se não
 * houver (nem mapeamento de chave, nem imagem cadastrada pra ela ainda).
 * `null` nunca deve virar `<img src="">`/CSS quebrado — quem consome isto
 * (LeagueCard) trata `null` caindo no gradiente atual como HERO_DEFAULT
 * temporário, até uma imagem de PADRÃO real ser fornecida (item 11).
 */
export function getCompetitionHero(competitionId: string): string | null {
  const key = getHeroKeyForCompetition(competitionId);
  if (!key) return null;
  return HERO_IMAGES[key] ?? null;
}
